import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ListChecks,
  Play,
  Clock,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Zap,
  Gauge,
  TrendingUp,
  DollarSign,
  RefreshCw,
  Filter,
  Pause,
  Settings,
  Brain,
  Activity,
  Workflow,
  Sliders,
  ChevronRight,
  CalendarClock,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useFleetStore } from '@/stores/fleet';
import type { FleetConfig } from '@/services/api';
import { ActivityFeed } from '@/components/knowledge/fleet/ActivityFeed';
import { NVIProjectionsPanel } from '@/components/knowledge/fleet/NVIProjectionsPanel';
import { ScoringWeightEditor } from '@/components/knowledge/fleet/ScoringWeightEditor';
import { HarvesterScheduler } from '@/components/knowledge/fleet/HarvesterScheduler';

function timeAgo(isoStr?: string): string {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

const STATUS_COLORS: Record<string, string> = {
  running: 'bg-brand-teal/10 text-brand-teal-dark border-brand-teal/20',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  queued: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={clsx(
        'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border',
        STATUS_COLORS[status] ?? 'bg-slate-50 text-slate-500 border-slate-200',
      )}
    >
      {status}
    </span>
  );
}

function MiniBar({
  label,
  value,
  color = 'bg-brand-teal',
}: {
  label: string;
  value: number;
  color?: string;
}) {
  const pct = Math.min(Math.max(value * 100, 0), 100);
  return (
    <div className="space-y-0.5">
      <div className="flex justify-between text-[10px] text-slate-500">
        <span>{label}</span>
        <span className="font-medium text-navy-900">{pct.toFixed(0)}%</span>
      </div>
      <div className="bg-slate-100 rounded-full h-1.5 overflow-hidden">
        <div
          className={clsx('h-full rounded-full transition-all duration-500', color)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div className="stat-card">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={accent ? 'text-brand-teal' : 'text-slate-400'} />
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="text-2xl font-bold text-navy-900">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fleet Controls Panel (operational knobs — preserved from QueueControlPage)
// ---------------------------------------------------------------------------

function FleetControlsPanel({
  config,
  loading,
  onUpdate,
}: {
  config: FleetConfig | null;
  loading: boolean;
  onUpdate: (c: Omit<FleetConfig, 'updated_at' | 'source'>) => void;
}) {
  const [local, setLocal] = useState<Omit<FleetConfig, 'updated_at' | 'source'> | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (config && !dirty) {
      setLocal({
        client_id: config.client_id,
        paused: config.paused,
        max_agents_per_cycle: config.max_agents_per_cycle,
        daily_budget_usd: config.daily_budget_usd,
        min_task_priority: config.min_task_priority,
        max_probe_batches: config.max_probe_batches,
        max_research_depth: config.max_research_depth,
        scoring_weight_overrides: config.scoring_weight_overrides ?? {},
      });
    }
  }, [config, dirty]);

  if (!local) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-3">
          <Settings size={16} className="text-slate-400" />
          <h2 className="font-semibold text-navy-900 text-sm">Fleet Controls</h2>
        </div>
        <p className="text-sm text-slate-400 text-center py-4">
          Select a client to configure fleet controls.
        </p>
      </div>
    );
  }

  const field = <K extends keyof typeof local>(key: K, value: (typeof local)[K]) => {
    setLocal((prev) => (prev ? { ...prev, [key]: value } : prev));
    setDirty(true);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Settings size={16} className="text-slate-400" />
          <h2 className="font-semibold text-navy-900 text-sm">Fleet Controls</h2>
          {config?.source === 'defaults' && (
            <span className="text-[9px] bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">
              defaults
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => field('paused', !local.paused)}
            className={clsx(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors border',
              local.paused
                ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
            )}
          >
            {local.paused ? <Play size={11} /> : <Pause size={11} />}
            {local.paused ? 'Resume' : 'Pause'}
          </button>
          {dirty && (
            <button
              onClick={() => {
                onUpdate(local);
                setDirty(false);
              }}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-brand-teal text-navy-900 hover:bg-brand-teal/90 disabled:opacity-40 transition-colors"
            >
              {loading ? <Loader2 size={11} className="animate-spin" /> : null}
              Save
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
            Max Agents / Cycle
            <span className="ml-1 text-navy-900 font-bold">{local.max_agents_per_cycle}</span>
          </label>
          <input
            type="range"
            min={1}
            max={50}
            step={1}
            value={local.max_agents_per_cycle}
            onChange={(e) => field('max_agents_per_cycle', Number(e.target.value))}
            className="w-full accent-brand-teal h-1.5"
          />
        </div>

        <div>
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
            Daily Budget (USD / client)
          </label>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
            <input
              type="number"
              min={0}
              step={1}
              value={local.daily_budget_usd}
              onChange={(e) => field('daily_budget_usd', Number(e.target.value))}
              className="w-full pl-5 pr-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-teal"
            />
          </div>
        </div>

        <div>
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
            Max Probe Batches
          </label>
          <input
            type="number"
            min={1}
            max={500}
            step={5}
            value={local.max_probe_batches}
            onChange={(e) => field('max_probe_batches', Number(e.target.value))}
            className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-teal"
          />
        </div>

        <div>
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
            Max Research Depth
          </label>
          <input
            type="number"
            min={1}
            max={10}
            step={1}
            value={local.max_research_depth}
            onChange={(e) => field('max_research_depth', Number(e.target.value))}
            className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-teal"
          />
        </div>

        <div className="col-span-2">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
            Min Task Priority
            <span className="ml-1 text-navy-900 font-bold">
              {local.min_task_priority.toFixed(2)}
            </span>
            <span className="ml-1 text-slate-400 font-normal">(skip tasks below this score)</span>
          </label>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={local.min_task_priority}
            onChange={(e) => field('min_task_priority', Number(e.target.value))}
            className="w-full accent-brand-teal h-1.5"
          />
        </div>
      </div>

      {local.paused && (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <Pause size={12} />
          Autonomous research is paused for this client. New completions will not trigger follow-up tasks.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FMQ Sweep Progress
// ---------------------------------------------------------------------------

function FmqSweepPanel({
  progress,
  maxBatches,
}: {
  progress: { probed: number; unprobed: number; total: number } | null;
  maxBatches: number;
}) {
  if (!progress) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-3">
          <Brain size={16} className="text-brand-purple" />
          <h2 className="font-semibold text-navy-900 text-sm">FMQ Sweep Progress</h2>
        </div>
        <p className="text-sm text-slate-400 text-center py-4">No FMQ data available</p>
      </div>
    );
  }

  const { probed, unprobed, total } = progress;
  const pct = total > 0 ? (probed / total) * 100 : 0;
  const batchSize = 50;
  const batchesRemaining = Math.ceil(unprobed / batchSize);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        <Brain size={16} className="text-brand-purple" />
        <h2 className="font-semibold text-navy-900 text-sm">FMQ Sweep Progress</h2>
      </div>
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-slate-500">Probed</span>
            <span className="text-sm font-bold text-navy-900">
              {probed.toLocaleString()} / {total.toLocaleString()}
              <span className="ml-1 text-xs text-slate-400 font-normal">({pct.toFixed(1)}%)</span>
            </span>
          </div>
          <div className="bg-slate-100 rounded-full overflow-hidden h-2.5">
            <div
              className="h-full rounded-full bg-brand-purple transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div>
            <span className="text-slate-400 uppercase text-[10px] font-bold block">Unprobed</span>
            <p className="font-bold text-navy-900">{unprobed.toLocaleString()}</p>
          </div>
          <div>
            <span className="text-slate-400 uppercase text-[10px] font-bold block">
              Batches Left
            </span>
            <p className="font-bold text-navy-900">{batchesRemaining}</p>
          </div>
          <div>
            <span className="text-slate-400 uppercase text-[10px] font-bold block">Max Batches</span>
            <p className="font-bold text-navy-900">{maxBatches}</p>
          </div>
        </div>
        {batchesRemaining > maxBatches && (
          <div className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            ⚠ Remaining batches ({batchesRemaining}) exceed max_probe_batches ({maxBatches}).
            Increase the limit in Fleet Controls to probe all FMQs.
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recent Lineage Chains
// ---------------------------------------------------------------------------

function RecentChainsPanel() {
  const { recentRoots, fetchRecentRoots } = useFleetStore();

  useEffect(() => {
    fetchRecentRoots();
  }, [fetchRecentRoots]);

  if (recentRoots.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-3">
          <Workflow size={16} className="text-slate-400" />
          <h2 className="font-semibold text-navy-900 text-sm">Recent Chains</h2>
        </div>
        <p className="text-sm text-slate-400 text-center py-4">
          No recent lineage roots.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Workflow size={16} className="text-brand-teal" />
          <h2 className="font-semibold text-navy-900 text-sm">Recent Chains</h2>
        </div>
        <Link
          to="/knowledge/estate/lineage"
          className="text-xs text-brand-teal hover:text-brand-teal-dark"
        >
          View all →
        </Link>
      </div>
      <div className="space-y-1.5">
        {recentRoots.slice(0, 10).map((r) => (
          <Link
            key={r.id}
            to={`/knowledge/estate/lineage/${r.id}`}
            className="flex items-center gap-2 text-xs border border-slate-200 rounded-lg px-3 py-2 hover:border-brand-teal/50 hover:bg-brand-teal/5 transition-colors"
          >
            {r.research_type && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                {r.research_type}
              </span>
            )}
            <span className="text-slate-700 truncate">
              {r.label || r.target_entity_name || r.target_entity_type || r.id.slice(0, 8)}
            </span>
            {r.client_id && (
              <span className="text-slate-500 truncate max-w-[6rem]">{r.client_id}</span>
            )}
            <span className="ml-auto text-slate-400 flex-shrink-0">
              {timeAgo(r.created_at ?? undefined)}
            </span>
            <ChevronRight size={12} className="text-slate-400 flex-shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Fleet Dashboard
// ---------------------------------------------------------------------------

type FleetTab = 'overview' | 'nvi' | 'activity' | 'chains' | 'schedules';

const TABS: Array<{ id: FleetTab; label: string; Icon: React.ElementType }> = [
  { id: 'overview', label: 'Overview', Icon: Gauge },
  { id: 'nvi', label: 'NVI & Weights', Icon: Sliders },
  { id: 'activity', label: 'Activity', Icon: Activity },
  { id: 'chains', label: 'Chains', Icon: Workflow },
  { id: 'schedules', label: 'Schedules', Icon: CalendarClock },
];

export function FleetDashboardPage({
  defaultTab = 'overview',
}: {
  defaultTab?: FleetTab;
}) {
  const {
    status,
    tasks,
    budget,
    nvi,
    fleetConfig,
    fmqProgress,
    taskFilter,
    loading,
    fleetConfigLoading,
    error,
    activeClient,
    fetchAll,
    fetchTasks,
    setTaskFilter,
    setActiveClient,
    triggerDispatch,
    updateFleetConfig,
  } = useFleetStore();

  const [dispatching, setDispatching] = useState(false);
  const [tab, setTab] = useState<FleetTab>(defaultTab);

  useEffect(() => {
    setTab(defaultTab);
  }, [defaultTab]);

  // Seed activeClient with whichever client appears first (fleet config or top task).
  useEffect(() => {
    if (!activeClient) {
      const seed = fleetConfig?.client_id ?? tasks[0]?.client_id ?? '';
      if (seed) setActiveClient(seed);
    }
  }, [activeClient, fleetConfig, tasks, setActiveClient]);

  useEffect(() => {
    fetchAll(activeClient || undefined);
    fetchTasks();
    const id = setInterval(() => fetchAll(activeClient || undefined), 15_000);
    return () => clearInterval(id);
  }, [fetchAll, fetchTasks, activeClient]);

  const handleDispatch = async () => {
    setDispatching(true);
    await triggerDispatch();
    setDispatching(false);
  };

  const nviScore = nvi?.nvi_score ?? nvi?.nvi_current ?? 0;
  const nviTarget = nvi?.nvi_target;
  const nviComponents = nvi?.components;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900">Autonomous Fleet</h1>
          <p className="text-sm text-slate-500 mt-1">
            Monitor task queues, budget utilization, NVI scoring, and the autonomous research fleet.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <input
            type="text"
            placeholder="Client ID"
            value={activeClient}
            onChange={(e) => setActiveClient(e.target.value)}
            className="px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-teal w-44"
          />
          {activeClient && (
            <Link
              to={`/knowledge/fleet/${encodeURIComponent(activeClient)}`}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              Open client drill-down
              <ChevronRight size={12} />
            </Link>
          )}
          <button
            onClick={handleDispatch}
            disabled={dispatching}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-teal text-navy-900 font-semibold text-sm rounded-lg hover:bg-brand-teal/90 disabled:opacity-40 transition-colors shadow-sm"
          >
            {dispatching ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            Trigger Dispatch
          </button>
          <button
            onClick={() => {
              fetchAll(activeClient || undefined);
              fetchTasks(taskFilter);
            }}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 text-slate-600 text-sm rounded-lg hover:bg-slate-50 transition-colors"
          >
            <RefreshCw size={14} className={clsx(loading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors',
              tab === t.id
                ? 'text-brand-teal border-b-2 border-brand-teal'
                : 'text-slate-500 hover:text-slate-700',
            )}
          >
            <t.Icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={ListChecks} label="Total Tasks" value={status?.queue_depth?.total ?? 0} />
            <StatCard icon={Clock} label="Ready" value={status?.queue_depth?.ready ?? 0} />
            <StatCard icon={Play} label="Executing" value={status?.queue_depth?.executing ?? 0} accent />
            <StatCard icon={CheckCircle2} label="Dispatched" value={status?.queue_depth?.dispatched ?? 0} />
          </div>

          <FleetControlsPanel
            config={fleetConfig}
            loading={fleetConfigLoading}
            onUpdate={updateFleetConfig}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign size={16} className="text-brand-teal" />
                <h2 className="font-semibold text-navy-900 text-sm">Cost Monitor</h2>
              </div>
              {budget ? (
                (() => {
                  const util = budget.global_utilization ?? 0;
                  const remainingCost = budget.global_headroom?.cost_usd;
                  const dailyBudget = fleetConfig?.daily_budget_usd;
                  return (
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-slate-500">Daily Utilization</span>
                          <span className="text-sm font-bold text-navy-900">
                            {Math.round(util * 100)}%
                          </span>
                        </div>
                        <div className="bg-slate-100 rounded-full overflow-hidden h-3">
                          <div
                            className={clsx(
                              'h-full rounded-full transition-all duration-500',
                              util > 0.9
                                ? 'bg-rose-400'
                                : util > 0.7
                                ? 'bg-amber-400'
                                : 'bg-brand-teal',
                            )}
                            style={{ width: `${Math.min(util * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="text-slate-400 uppercase text-[10px] font-bold">
                            Daily Budget
                          </span>
                          <p className="font-bold text-navy-900 mt-0.5">
                            {dailyBudget != null ? `$${dailyBudget.toFixed(2)}` : '—'}
                          </p>
                        </div>
                        <div>
                          <span className="text-slate-400 uppercase text-[10px] font-bold">
                            Remaining (global)
                          </span>
                          <p className="font-bold text-navy-900 mt-0.5">
                            {remainingCost != null ? `$${remainingCost.toLocaleString()}` : '—'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-[10px] text-slate-400">
                        <span className="flex items-center gap-1">
                          <Activity size={10} />
                          Active: <span className="text-navy-900 font-medium">{budget.active_tasks ?? 0}</span>
                        </span>
                        <span>
                          Dispatched:{' '}
                          <span className="text-navy-900 font-medium">
                            {budget.total_dispatched ?? 0}
                          </span>
                        </span>
                        <span>
                          Completed:{' '}
                          <span className="text-navy-900 font-medium">
                            {budget.total_completed ?? 0}
                          </span>
                        </span>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">No budget data available</p>
              )}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={16} className="text-brand-purple" />
                <h2 className="font-semibold text-navy-900 text-sm">NVI Score</h2>
              </div>
              {nvi ? (
                <div className="space-y-3">
                  <div className="flex items-end gap-3">
                    <span className="text-3xl font-bold text-navy-900">{nviScore.toFixed(1)}</span>
                    {nviTarget != null && (
                      <span className="text-sm text-slate-400 mb-1">/ {nviTarget.toFixed(1)} target</span>
                    )}
                  </div>
                  {nviTarget != null && (
                    <div className="bg-slate-100 rounded-full overflow-hidden h-2">
                      <div
                        className="h-full rounded-full bg-brand-purple transition-all duration-500"
                        style={{ width: `${Math.min((nviScore / nviTarget) * 100, 100)}%` }}
                      />
                    </div>
                  )}
                  {nviComponents && (
                    <div className="space-y-1.5 pt-1">
                      {(
                        [
                          ['Coverage', nviComponents.coverage, 'bg-sky-400'],
                          ['Density', nviComponents.density, 'bg-violet-400'],
                          ['Diversity', nviComponents.diversity, 'bg-emerald-400'],
                          ['Leverage', nviComponents.leverage, 'bg-amber-400'],
                          ['Freshness', nviComponents.freshness, 'bg-rose-400'],
                        ] as [string, number | undefined, string][]
                      ).map(([label, val, color]) =>
                        val != null ? (
                          <MiniBar key={label} label={label} value={val} color={color} />
                        ) : null,
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">No NVI data available</p>
              )}
            </div>
          </div>

          <FmqSweepPanel progress={fmqProgress} maxBatches={fleetConfig?.max_probe_batches ?? 30} />

          {status?.dispatch_rate_last_hour != null && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-4">
              <Gauge size={20} className="text-brand-teal" />
              <div>
                <p className="text-[10px] text-slate-400 uppercase font-bold">Dispatch Rate</p>
                <p className="text-lg font-bold text-navy-900">
                  {status.dispatch_rate_last_hour}{' '}
                  <span className="text-xs text-slate-400 font-normal">tasks/hour</span>
                </p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-navy-900 uppercase tracking-wide">
                Tasks
                <span className="ml-2 text-xs text-slate-400 font-normal">({tasks.length})</span>
              </h2>
              <div className="flex items-center gap-2">
                <Filter size={12} className="text-slate-400" />
                {['all', 'pending', 'running', 'completed', 'failed'].map((f) => (
                  <button
                    key={f}
                    onClick={() => setTaskFilter(f)}
                    className={clsx(
                      'px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all border',
                      taskFilter === f
                        ? 'bg-brand-teal/10 border-brand-teal/30 text-brand-teal-dark'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300',
                    )}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {tasks.length > 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                        Task ID
                      </th>
                      <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                        Priority
                      </th>
                      <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {tasks.map((t) => (
                      <tr key={t.task_id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono text-[10px] text-brand-purple">
                          {t.task_id}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{t.task_type ?? '-'}</td>
                        <td className="px-4 py-3 font-medium text-navy-900">
                          {t.client_id ?? '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={clsx(
                              'text-[10px] font-bold px-2 py-0.5 rounded-full',
                              t.priority >= 8
                                ? 'bg-rose-50 text-rose-600'
                                : t.priority >= 5
                                ? 'bg-amber-50 text-amber-600'
                                : 'bg-slate-100 text-slate-500',
                            )}
                          >
                            P{t.priority}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={t.status} />
                        </td>
                        <td className="px-4 py-3 text-slate-400">{timeAgo(t.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center shadow-sm">
                <ListChecks size={32} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-400">No tasks match the current filter</p>
              </div>
            )}
          </div>

          {status?.top_pending && status.top_pending.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <h2 className="font-semibold text-navy-900 text-sm mb-3 flex items-center gap-2">
                <Clock size={14} className="text-amber-500" />
                Top Pending Tasks
              </h2>
              <div className="space-y-2">
                {status.top_pending.map((t) => {
                  const priority = t.priority_score ?? 0;
                  const priorityPct = (priority * 100).toFixed(0);
                  return (
                    <div
                      key={t.id}
                      className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={clsx(
                            'text-[10px] font-bold px-2 py-0.5 rounded-full',
                            priority >= 0.8
                              ? 'bg-rose-50 text-rose-600'
                              : priority >= 0.5
                              ? 'bg-amber-50 text-amber-600'
                              : 'bg-slate-100 text-slate-500',
                          )}
                        >
                          {priorityPct}%
                        </span>
                        <span className="font-mono text-[10px] text-brand-purple">{t.id}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        {t.research_type && <span>{t.research_type}</span>}
                        {t.target && <span className="text-slate-500">{t.target}</span>}
                        {t.client_id && (
                          <span className="font-medium text-navy-900">{t.client_id}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'nvi' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <NVIProjectionsPanel />
          <ScoringWeightEditor clientId={activeClient} />
        </div>
      )}

      {tab === 'activity' && (
        <ActivityFeed clientIdFilter={activeClient || undefined} />
      )}

      {tab === 'chains' && <RecentChainsPanel />}

      {tab === 'schedules' && <HarvesterScheduler clientId={activeClient} />}

      {loading && (
        <p className="text-[10px] text-slate-400 font-mono text-center">refreshing...</p>
      )}
    </div>
  );
}
